import React, { useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { Post } from "../types/Post";
import CustomMarkdown from "../utils/CustomMarkdown";
import ReplyInput from "./ReplyInput";
import { getRelativeTime } from "../utils/time";
import { addLove, removeLove } from "../api/post";
import { ReactComponent as HeartSVG } from "../assets/icons/heart.svg";
import { ReactComponent as ReplySVG } from "../assets/icons/reply.svg";

const PostCard: React.FC<{ post: Post }> = ({ post }) => {
  const [isLoved, setIsLoved] = useState(false);
  const [loveCount, setLoveCount] = useState(post.love_count);
  const [showReplyInput, setShowReplyInput] = useState(false);
  const [replyCount, setReplyCount] = useState(post.reply_count);

  const handleReplyClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowReplyInput(!showReplyInput);
  };

  const handleLoveClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      if (isLoved) {
        await removeLove(post.id);
        setIsLoved(false);
        setLoveCount((prev) => prev - 1);
      } else {
        await addLove(post.id);
        setIsLoved(true);
        setLoveCount((prev) => prev + 1);
      }
    } catch (error) {
      console.error("Failed to toggle love:", error);
    }
  };

  return (
    <PostCardContainer>
      <BackgroundLink to={`/posts/${post.id}`} /> {/* ← カード全体を覆う */}
      <Header>
        <HeaderIconLink to={`/users/${post.author.scratch_name}`}>
          <UserIcon src={post.author.icon_url} alt="icon" />
        </HeaderIconLink>
        <HeaderNameLink to={`/users/${post.author.scratch_name}`}>
          <UserName>
            {post.author.display_name || post.author.scratch_name}
          </UserName>
        </HeaderNameLink>
        <Date>- {getRelativeTime(post.created_at)}</Date>
      </Header>
      <Body>
        <Content>
          <CustomMarkdown>{post.content}</CustomMarkdown>
        </Content>
      </Body>
      <Actions>
        <Action onClick={handleReplyClick}>
          <ReplyButton onClick={handleReplyClick}>
            <ReplySVG />
            <span>{replyCount}</span>
          </ReplyButton>
        </Action>
        <Action onClick={handleLoveClick}>
          <HeartButton isLoved={isLoved}>
            <HeartSVG width={18} height={18} />
            <span>{loveCount}</span>
          </HeartButton>
        </Action>
      </Actions>
      {showReplyInput && (
        <ReplyInputWrapper>
          <ReplyInput
            postId={post.id}
            onReplySuccess={() => {
              setReplyCount((prev) => prev + 1);
              setShowReplyInput(false);
            }}
            onCancel={() => setShowReplyInput(false)}
          />
        </ReplyInputWrapper>
      )}
    </PostCardContainer>
  );
};

const PostCardContainer = styled.div`
  background: rgb(250, 250, 250);
  border-radius: 24px;
  padding: 0;
  width: 100%;
  max-width: 900px;
  margin: 20px auto 0;
  box-shadow: none;
  box-sizing: border-box;
  transition: background-color 0.2s ease, box-shadow 0.2s ease;
  position: relative;

  &:hover {
    box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1); // ← ホバー時の影
  }
`;

const BackgroundLink = styled(Link)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
`;

const Date = styled.span`
  color: #bbb;
  font-size: 12px;
  margin-left: 0px; // ユーザー名との間隔
  display: inline-block;
  line-height: 36px; // アイコンと縦揃え
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  border-radius: 24px 24px 0 0;
  padding: 16px 24px 12px 24px;
  box-sizing: border-box;
`;

const HeaderIconLink = styled(Link)`
  display: inline-block;
  z-index: 1;
  margin-right: 0px; // アイコンと名前の間隔
`;

const HeaderNameLink = styled(Link)`
  display: inline-block;
  font-weight: bold;
  z-index: 1;
  padding: 4px 8px; // 上下4px、左右8px のクリック可能領域を追加
  border-radius: 4px; // 見た目の角丸もあると自然
  text-decoration: none;
  color: inherit;
`;

const UserIcon = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 7px;
  transition: box-shadow 0.2s ease;
  &:hover {
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.15); // ← ホバー時の影
  }
`;

const UserName = styled.span`
  display: inline-block;
  margin-left: -5px;
  margin-bottom: 2px;
  font-weight: bold;
  z-index: 5;
  transition: text-shadow 0.2s ease;
  &:hover {
    text-shadow: 0 3px 10px rgba(0, 0, 0, 0.15); // ← ホバー時の影
  }
`;

const Body = styled.div`
  padding: 0 24px 12px 24px;
  position: relative;
`;

const Content = styled.div`
  color: rgb(39, 39, 39);
  font-size: 15px;
  position: relative;
  z-index: 4;
  width: fit-content;
  margin-top: -7px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: 60px;
  padding: 0 24px 12px 24px;
  color: rgb(74, 74, 74);
  z-index: 1;
`;

const Action = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: color 0.2s ease;
  font-size: 18px;
  z-index: 1;

  &:hover {
    color: rgb(150, 150, 150);
  }

  span {
    margin-left: 7px;
    font-weight: bold;
  }
`;

const HeartButton = styled.div<{ isLoved: boolean }>`
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const ReplyButton = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: rgb(150, 150, 150);
  }

  svg {
    width: 18px;
    height: 18px;
    fill: currentColor;
    transition: fill 0.2s ease;
  }
`;

const ReplyInputWrapper = styled.div`
  z-index: 4;
  position: relative;
`;

export default PostCard;
